"use client";

import { useEffect, useRef, useState } from "react";
import AppImage from "@/components/ui/AppImage";

const STEPS = [
{
  number: "01",
  titleEn: "Free Consultation",
  titleZh: "免費諮詢",
  descEn: "Contact us via WhatsApp or visit our Mong Kok office. We discuss your vision, budget, and timeline. Site visit is included at no charge.",
  descZh: "透過WhatsApp聯絡我們或親臨旺角辦公室。我們討論您的設計理念、預算及工期。免費上門量度及評估。",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1005fab09-1766507938911.png",
  alt: "Interior designer consulting with client over renovation plans at a meeting table"
},
{
  number: "02",
  titleEn: "Design & Planning",
  titleZh: "設計規劃",
  descEn: "Our designers create detailed 3D renders and floor plans. Material selections, color palettes, and custom furniture designs are presented for your approval.",
  descZh: "設計師製作詳細3D效果圖及平面圖，材料選擇、色彩方案及訂造傢俬設計，供您審核及確認。",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1c23e77b4-1764661943205.png",
  alt: "Architect and designer reviewing 3D floor plans and material samples on desk"
},
{
  number: "03",
  titleEn: "Transparent Quotation",
  titleZh: "透明報價",
  descEn: "Receive a comprehensive itemized quotation with no hidden costs. We explain every line item and work within your approved budget.",
  descZh: "收到全面分項報價，無隱藏費用。我們解釋每個項目，並在您批准的預算內施工。",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1d5e5d52c-1771173308616.png",
  alt: "Contractor reviewing detailed renovation quotation documents with client"
},
{
  number: "04",
  titleEn: "Construction",
  titleZh: "施工階段",
  descEn: "Master craftsmen execute the work with precision. Weekly progress reports and site photos keep you informed. Strict quality checks at every milestone.",
  descZh: "師傅精確施工，每週進度報告及現場照片讓您隨時掌握工程進展，每個里程碑嚴格質量檢查。",
  image: "https://images.unsplash.com/photo-1633880222168-4c74b89a81e0",
  alt: "Construction workers installing flooring and ceiling during active renovation"
},
{
  number: "05",
  titleEn: "Handover & Aftercare",
  titleZh: "交樓及售後",
  descEn: "Final inspection walkthrough with you before handover. We provide a 2-year workmanship warranty and dedicated aftercare support.",
  descZh: "交樓前與您一起進行最終驗收。提供2年工程保修及專屬售後服務支援。",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_172823dd0-1764714446049.png",
  alt: "Happy client receiving keys to newly renovated Hong Kong apartment"
}];


export default function ProcessSection() {
  const [activeStep, setActiveStep] = useState(0);
  const stepsRef = useRef<(HTMLDivElement | null)[]>([]);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const elements = sectionRef.current?.querySelectorAll(".proc-reveal");
    if (!elements) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.remove("hidden-reveal");
        });
      },
      { threshold: 0.1 }
    );
    elements.forEach((el) => {
      el.classList.add("hidden-reveal");
      observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const stepObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const idx = stepsRef.current.indexOf(entry.target as HTMLDivElement);
            if (idx !== -1) setActiveStep(idx);
          }
        });
      },
      { rootMargin: "-35% 0% -55% 0%", threshold: 0 }
    );
    stepsRef.current.forEach((el) => {if (el) stepObserver.observe(el);});
    return () => stepObserver.disconnect();
  }, []);

  return (
    <section
      id="process"
      ref={sectionRef}
      className="py-24 md:py-32 px-6 md:px-12"
      style={{ background: "linear-gradient(180deg, #0A1628 0%, #0D1E3A 100%)" }}>
      
      <div className="max-w-screen-xl mx-auto">
        {/* Header */}
        <div className="text-center mb-20 proc-reveal reveal">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="w-8 h-[1px]" style={{ background: "#D4AF37" }} />
            <span className="text-xs font-bold uppercase tracking-[0.2em]" style={{ color: "#D4AF37" }}>
              Our Process · 裝修流程
            </span>
            <div className="w-8 h-[1px]" style={{ background: "#D4AF37" }} />
          </div>
          <h2 className="font-display section-title text-white">
            由諮詢到交樓<br />
            <span className="italic" style={{ color: "#D4AF37" }}>5 Simple</span> Steps
          </h2>
        </div>

        {/* Sticky Scroll Layout */}
        <div className="flex flex-col lg:flex-row gap-16 items-start">
          {/* Steps (Left) */}
          <div className="w-full lg:w-1/2 space-y-16">
            {STEPS.map((step, index) =>
            <div
              key={step.number}
              ref={(el) => {stepsRef.current[index] = el;}}
              className={`process-step proc-reveal reveal ${activeStep === index ? "active" : ""}`}>
              
                <div className="flex items-start gap-5">
                  {/* Number */}
                  <div className="flex-shrink-0 flex flex-col items-center">
                    <div
                    className="w-12 h-12 rounded-full flex items-center justify-center font-display font-bold text-sm transition-all duration-500"
                    style={{
                      background: activeStep === index ?
                      "linear-gradient(135deg, #D4AF37, #A8891E)" :
                      "rgba(26,47,94,0.5)",
                      color: activeStep === index ? "#0A1628" : "rgba(245,240,232,0.4)",
                      border: activeStep === index ? "none" : "1px solid rgba(212,175,55,0.2)"
                    }}>
                    
                      {step.number}
                    </div>
                    {index < STEPS.length - 1 &&
                  <div
                    className="w-[1px] h-16 mt-3"
                    style={{ background: "linear-gradient(to bottom, rgba(212,175,55,0.4), transparent)" }} />

                  }
                  </div>
                  {/* Content */}
                  <div className="flex-1 pt-2">
                    <div className="text-xs font-bold uppercase tracking-widest mb-2" style={{ color: "#D4AF37" }}>
                      {step.titleZh}
                    </div>
                    <h3 className="font-display text-2xl font-bold text-white mb-3">{step.titleEn}</h3>
                    <p className="text-sm leading-relaxed" style={{ color: "rgba(245,240,232,0.65)" }}>
                      {step.descEn}
                    </p>
                    <p className="text-xs leading-relaxed mt-2" style={{ color: "rgba(245,240,232,0.4)" }}>
                      {step.descZh}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Sticky Image (Right) */}
          <div className="w-full lg:w-1/2 lg:sticky lg:top-24 h-[500px] rounded-2xl overflow-hidden">
            {STEPS.map((step, index) =>
            <div
              key={step.number}
              className="absolute inset-0 transition-opacity duration-700"
              style={{ opacity: activeStep === index ? 1 : 0 }}>
              
                <AppImage
                src={step.image}
                alt={step.alt}
                fill
                className="object-cover" />
              
                <div
                className="absolute inset-0"
                style={{ background: "linear-gradient(to top, rgba(10,22,40,0.8) 0%, rgba(10,22,40,0.2) 60%, transparent 100%)" }} />
              
                <div className="absolute bottom-6 left-6">
                  <div
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wider"
                  style={{ background: "rgba(212,175,55,0.15)", color: "#D4AF37", border: "1px solid rgba(212,175,55,0.3)" }}>
                  
                    Step {step.number} · {step.titleZh}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>);

}